/** Left road section of the LUDOKA game board */

import type { RoadSectionProps } from '../../constants/types';
import { StarDecoration } from './StarDecoration';
import {
  GreenDarkGradient,
  GreenLightGradient,
  BrownGroundGradient,
  GrassOverlayGradient,
  YellowStretchGradient
} from './BoardGradients';
import { ROAD_COLORS } from './constants/colors';
import { renderMultiplePositions } from './utils/utils';
import { LEFT_CENTER_SQUARE_BOUNDS as CENTER_SQUARE_BOUNDS } from './constants/center-square-bounds';
import type { BoardPosition } from '../../constants/positions';

/**
 * Render order for left section (Yellow)
 * Ordered left to right for proper 3D overlap
 */
const RENDER_ORDER: BoardPosition[] = [
  'YS1',
  'YC1',
  'YF1',
  'YS2',
  'YC2',
  'YF2',
  'YS3',
  'YC3',
  'YF3',
  'YS4',
  'YC4',
  'YF4',
  'YS5',
  'YC5',
  'YF5',
  'YS6',
  'YC6',
  'YF6',
  'YFINAL'
];

const SCALE_MULTIPLIER = 1.5; // Horizontal section needs larger pawns

export default function LeftRoadSection({
  viewBox = '0 0 616 268',
  className = '',
  pawns = [],
  animatingPawnIds,
  kickingPawnIds,
  rollbackPawnIds,
  flippingPawnIds,
  onPawnClick,
  clickablePawnIds,
  pawnComponents
}: RoadSectionProps) {
  return (
    <svg
      viewBox={viewBox}
      className={className}
      overflow="visible"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Left road section with yellow final stretch"
    >
      {/* Top left corner - gray placeholder */}
      <path
        d="M21.3999 11.73H88.2399V88.57H11.3999V21.73C11.3999 16.21 15.8799 11.73 21.3999 11.73Z"
        fill={ROAD_COLORS.placeholderGray}
      />

      {/* Column 1: Yellow final stretch and white center path */}
      <path
        d="M165.08 11.73H88.2402V88.57H165.08V11.73Z"
        fill={ROAD_COLORS.yellow.dark}
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M150.69 94.91H102.64C98.1887 94.91 94.5801 98.5186 94.5801 102.97V151.02C94.5801 155.471 98.1887 159.08 102.64 159.08H150.69C155.141 159.08 158.75 155.471 158.75 151.02V102.97C158.75 98.5186 155.141 94.91 150.69 94.91Z"
        fill={ROAD_COLORS.white}
      />
      <path d="M88.2399 88.57H11.3999V165.41H88.2399V88.57Z" fill={ROAD_COLORS.lightGray} />
      <path
        d="M165.08 88.57H88.2402V165.41H165.08V88.57Z"
        fill={ROAD_COLORS.yellow.bright}
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M150.69 94.91H102.64C98.1887 94.91 94.5801 98.5186 94.5801 102.97V151.02C94.5801 155.471 98.1887 159.08 102.64 159.08H150.69C155.141 159.08 158.75 155.471 158.75 151.02V102.97C158.75 98.5186 155.141 94.91 150.69 94.91Z"
        fill={ROAD_COLORS.white}
      />
      <path
        d="M150.69 18.07H102.64C98.1887 18.07 94.5801 21.6786 94.5801 26.13V74.18C94.5801 78.6314 98.1887 82.24 102.64 82.24H150.69C155.141 82.24 158.75 78.6314 158.75 74.18V26.13C158.75 21.6786 155.141 18.07 150.69 18.07Z"
        fill={ROAD_COLORS.white}
      />

      {/* Column 2: Yellow final stretch and white center path */}
      <path
        d="M241.92 88.57H165.08V165.41H241.92V88.57Z"
        fill={ROAD_COLORS.yellow.dark}
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M227.53 94.91H179.48C175.029 94.91 171.42 98.5186 171.42 102.97V151.02C171.42 155.471 175.029 159.08 179.48 159.08H227.53C231.981 159.08 235.59 155.471 235.59 151.02V102.97C235.59 98.5186 231.981 94.91 227.53 94.91Z"
        fill={ROAD_COLORS.white}
      />

      {/* Column 3: Yellow final stretch and white center path */}
      <path
        d="M318.76 88.57H241.92V165.41H318.76V88.57Z"
        fill={ROAD_COLORS.yellow.bright}
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M304.37 94.91H256.32C251.868 94.91 248.26 98.5186 248.26 102.97V151.02C248.26 155.471 251.868 159.08 256.32 159.08H304.37C308.821 159.08 312.43 155.471 312.43 151.02V102.97C312.43 98.5186 308.821 94.91 304.37 94.91Z"
        fill={ROAD_COLORS.white}
      />

      {/* Column 4: Yellow final stretch and white center path */}
      <path
        d="M395.61 88.57H318.77V165.41H395.61V88.57Z"
        fill={ROAD_COLORS.yellow.dark}
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M381.21 94.91H333.16C328.709 94.91 325.1 98.5186 325.1 102.97V151.02C325.1 155.471 328.709 159.08 333.16 159.08H381.21C385.661 159.08 389.27 155.471 389.27 151.02V102.97C389.27 98.5186 385.661 94.91 381.21 94.91Z"
        fill={ROAD_COLORS.white}
      />

      {/* Column 5: Yellow final stretch and white center path */}
      <path
        d="M472.45 88.57H395.61V165.41H472.45V88.57Z"
        fill={ROAD_COLORS.yellow.bright}
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />

      {/* Brown ground border - base layer */}
      <path
        d="M11.4399 228.96V254.54C11.8199 259 13.1399 265.01 17.8099 268.83C24.4199 274.23 34.0699 272.24 41.0999 271.22C59.6599 268.52 52.5099 273.71 95.6999 273.48C123.48 273.33 122.91 271.21 148.24 271.15C171.11 271.1 172.28 272.83 196.38 273.46C225.57 274.23 226.97 271.77 268.84 270.94C299.03 270.34 297.86 271.61 330.95 270.94C361.24 270.33 359.75 269.33 388.85 268.92C401.74 268.74 405.67 268.88 448.44 268.92C462.27 268.93 464.87 268.92 472.32 268.92L472.44 241.53L11.4399 228.96Z"
        fill="url(#left_brown_ground)"
      />

      {/* Green grass overlay - top layer */}
      <path
        d="M472.39 229.11V256.5C464.95 256.5 462.27 256.51 448.45 256.5H446.97C443.72 256.49 440.69 256.49 437.88 256.48C436.1 256.48 434.39 256.48 432.77 256.48C428.55 256.48 424.86 256.46 421.6 256.46C409.11 256.43 402.91 256.39 397.34 256.42C394.86 256.43 392.51 256.45 389.79 256.49C389.48 256.49 389.17 256.5 388.85 256.5C387.88 256.51 386.94 256.53 386.03 256.54C382.64 256.6 379.69 256.67 377.07 256.73C359.43 257.22 356.44 258 330.95 258.51C318.24 258.77 310.59 258.74 304.27 258.64C301.99 258.61 299.88 258.56 297.77 258.52C295.8 258.48 293.83 258.44 291.71 258.41C289.85 258.38 287.86 258.36 285.67 258.36C282.64 258.34 279.24 258.36 275.16 258.41C274.93 258.41 274.7 258.41 274.46 258.41C272.71 258.43 270.84 258.46 268.84 258.5C268.66 258.5 268.48 258.5 268.3 258.5C267.43 258.52 266.57 258.54 265.73 258.56C226.88 259.45 224.82 261.76 196.38 261.02C183.24 260.67 176.92 260 170.18 259.47C166.58 259.19 162.86 258.94 157.92 258.81C157.67 258.8 157.42 258.8 157.17 258.79C154.58 258.72 151.65 258.7 148.24 258.71C145.85 258.71 143.69 258.73 141.72 258.77C126.8 259.06 122.44 260.22 109 260.76H108.98C106.06 260.87 102.7 260.97 98.7199 261.01C97.8199 261.03 96.8899 261.03 95.9299 261.04H95.6999C67.2599 261.18 60.6499 258.99 54.1599 258.24C52.0099 258 49.8799 257.91 46.9699 258.12C45.6899 258.2 44.2599 258.34 42.6099 258.56C42.1299 258.62 41.6199 258.69 41.0999 258.77C38.5599 259.14 35.6899 259.64 32.7399 259.86C27.5099 260.27 22.0399 259.84 17.8099 256.39C14.5199 253.7 12.8899 249.92 12.0799 246.36C11.7399 244.87 11.5499 243.42 11.4399 242.1V216.52L201.65 221.7L241.45 222.78L306.1 224.54L328.64 225.16H328.65L362.57 226.08H362.79H362.8L429.13 227.88L466.88 228.91L472.39 229.08V229.11Z"
        fill="url(#left_grass_overlay)"
      />

      {/* Column 6: White center path squares */}
      <path
        d="M458.05 94.91H410C405.549 94.91 401.94 98.5186 401.94 102.97V151.02C401.94 155.471 405.549 159.08 410 159.08H458.05C462.501 159.08 466.11 155.471 466.11 151.02V102.97C466.11 98.5186 462.501 94.91 458.05 94.91Z"
        fill={ROAD_COLORS.white}
      />
      <path
        d="M458.05 94.91H410C405.549 94.91 401.94 98.5186 401.94 102.97V151.02C401.94 155.471 405.549 159.08 410 159.08H458.05C462.501 159.08 466.11 155.471 466.11 151.02V102.97C466.11 98.5186 462.501 94.91 458.05 94.91Z"
        fill={ROAD_COLORS.white}
      />

      {/* Top row green gradient squares */}
      <path
        d="M318.76 11.73H241.92V88.57H318.76V11.73Z"
        fill="url(#left_paint2_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M472.45 11.73H395.61V88.57H472.45V11.73Z"
        fill="url(#left_paint3_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M241.92 11.73H165.08V88.57H241.92V11.73Z"
        fill="url(#left_paint4_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M395.61 11.73H318.77V88.57H395.61V11.73Z"
        fill="url(#left_paint5_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />

      {/* Bottom left corner - gray placeholder */}
      <path
        d="M11.3999 165.41H88.2399V242.25H21.3999C15.8799 242.25 11.3999 237.77 11.3999 232.25V165.41Z"
        fill={ROAD_COLORS.placeholderGray}
      />

      {/* Bottom row green gradient squares */}
      <path
        d="M165.08 165.41H88.2402V242.25H165.08V165.41Z"
        fill="url(#left_paint6_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M11.3999 165.41H88.2399V242.25H21.3999C15.8799 242.25 11.3999 237.77 11.3999 232.25V165.41Z"
        fill="url(#left_paint7_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M21.3999 11.73H88.2399V88.57H11.3999V21.73C11.3999 16.21 15.8799 11.73 21.3999 11.73Z"
        fill="url(#left_paint8_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M88.2399 88.57H11.3999V165.41H88.2399V88.57Z"
        fill="url(#left_paint9_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M318.76 165.41H241.92V242.25H318.76V165.41Z"
        fill="url(#left_paint10_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M472.45 165.41H395.61V242.25H472.45V165.41Z"
        fill="url(#left_paint11_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M241.92 165.41H165.08V242.25H241.92V165.41Z"
        fill="url(#left_paint12_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />
      <path
        d="M395.61 165.41H318.77V242.25H395.61V165.41Z"
        fill="url(#left_paint13_linear)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />

      {/* Star decoration */}
      <StarDecoration x={212.04} y={193.79} />

      {/* Yellow final stretch arrow on right */}
      <path
        d="M587.71 126.99L472.45 242.25L473.18 11.73L587.71 126.99Z"
        fill="url(#left_yellow_final_stretch)"
        stroke={ROAD_COLORS.black}
        strokeMiterlimit="10"
      />

      {/* Render pawns column by column from left to right for 3D overlap effect */}
      {renderMultiplePositions(
        RENDER_ORDER,
        pawns,
        CENTER_SQUARE_BOUNDS,
        undefined,
        SCALE_MULTIPLIER,
        animatingPawnIds,
        onPawnClick,
        clickablePawnIds,
        rollbackPawnIds,
        kickingPawnIds,
        pawnComponents,
        flippingPawnIds
      )}

      <defs>
        <BrownGroundGradient
          id="left_brown_ground"
          x1="241.94"
          y1="257.3"
          x2="241.94"
          y2="283.98"
        />
        <GrassOverlayGradient
          id="left_grass_overlay"
          x1="241.92"
          y1="260.86"
          x2="241.92"
          y2="243.41"
        />

        <GreenDarkGradient id="left_paint2_linear" x1="241.92" y1="50.15" x2="318.77" y2="50.15" />
        <GreenDarkGradient
          id="left_paint3_linear"
          x1="395.61"
          y1="-2.04313e-05"
          x2="472.45"
          y2="-2.04313e-05"
        />
        <GreenLightGradient id="left_paint4_linear" x1="165.08" y1="50.15" x2="241.92" y2="50.15" />
        <GreenLightGradient
          id="left_paint5_linear"
          x1="318.77"
          y1="-2.04313e-05"
          x2="395.61"
          y2="-2.04313e-05"
        />
        <GreenDarkGradient
          id="left_paint6_linear"
          x1="88.2402"
          y1="203.83"
          x2="165.08"
          y2="203.83"
        />
        <GreenLightGradient
          id="left_paint7_linear"
          x1="11.3999"
          y1="203.83"
          x2="88.2399"
          y2="203.83"
        />
        <GreenLightGradient
          id="left_paint8_linear"
          x1="11.3999"
          y1="50.15"
          x2="88.2399"
          y2="50.15"
        />
        <GreenDarkGradient
          id="left_paint9_linear"
          x1="11.3999"
          y1="126.99"
          x2="88.2399"
          y2="126.99"
        />
        <GreenDarkGradient
          id="left_paint10_linear"
          x1="-0.000124334"
          y1="203.83"
          x2="0.999876"
          y2="203.83"
        />
        <GreenDarkGradient
          id="left_paint11_linear"
          x1="395.61"
          y1="203.83"
          x2="472.45"
          y2="203.83"
        />
        <GreenLightGradient
          id="left_paint12_linear"
          x1="7.62939e-05"
          y1="203.83"
          x2="1.00008"
          y2="203.83"
        />
        <GreenLightGradient
          id="left_paint13_linear"
          x1="318.77"
          y1="203.83"
          x2="395.61"
          y2="203.83"
        />

        <YellowStretchGradient
          id="left_yellow_final_stretch"
          x1="472.45"
          y1="126.99"
          x2="587.71"
          y2="126.99"
        />
      </defs>
    </svg>
  );
}
